import type { ComponentProps } from 'react';
import { faPlus } from '@awesome.me/kit-4a6bf60416/icons/classic/regular';

import { AddPageModal } from '@/components/page-editor/add-page-modal';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { useIsPageEditor } from '@/hooks/use-is-page-editor';
import type {
  DataWidgetBlockType,
  DataWidgetStrategy,
} from '@/lib/blocks/collection/data-widget/blocks';
import { cmsReviewsOverviewPath } from '@/lib/routes';
import { PageType } from '@/types/page';

type EmptyStateConfig = {
  label: string;
  singularLabel: string;
} & (
  | { kind: 'page'; initialSidebarSelection: PageType; chainedChildPageType: PageType }
  | { kind: 'link'; getHref: () => string }
);

const EMPTY_STATE_CONFIG = new Map<DataWidgetBlockType, EmptyStateConfig>([
  [
    'blog_widget',
    {
      kind: 'page',
      label: 'Blog Posts',
      singularLabel: 'blog',
      initialSidebarSelection: PageType.BLOG_INDEX,
      chainedChildPageType: PageType.BLOG,
    },
  ],
  [
    'case_result_widget',
    {
      kind: 'page',
      label: 'Case Results',
      singularLabel: 'case result',
      initialSidebarSelection: PageType.CASE_RESULT_INDEX,
      chainedChildPageType: PageType.CASE_RESULT,
    },
  ],
  [
    'review_widget',
    {
      kind: 'link',
      label: 'Reviews',
      singularLabel: 'review',
      getHref: () => cmsReviewsOverviewPath(),
    },
  ],
]);

function unavailableMessage(
  config: EmptyStateConfig,
  {
    strategy,
    hasDynamicRecords,
    hasActiveFilters,
  }: { strategy?: DataWidgetStrategy; hasDynamicRecords: boolean; hasActiveFilters: boolean },
) {
  if (!hasDynamicRecords) return null;

  if (hasActiveFilters) {
    return {
      title: `No ${config.label.toLowerCase()} match your filters`,
      hint: `Adjust the filters in settings to see ${config.singularLabel}s displayed here.`,
    };
  }

  if (strategy === 'pinned_only') {
    return {
      title: `No pinned ${config.label.toLowerCase()} available`,
      hint: `Please pin ${config.singularLabel}s to see them displayed here.`,
    };
  }

  return null;
}

function AddRecordsButton({ label, ...props }: ComponentProps<typeof Button> & { label: string }) {
  return (
    <Button
      variant="link"
      className="gap-1.5 font-semibold no-underline hover:no-underline"
      {...props}
    >
      <Icon icon={faPlus} className="size-4" />
      Add {label}
    </Button>
  );
}

function AddRecordsLink({ label, href }: { label: string; href: string }) {
  return (
    <Button
      asChild
      variant="link"
      className="gap-1.5 font-semibold no-underline hover:no-underline"
    >
      <a href={href} target="_blank" rel="noopener noreferrer">
        <Icon icon={faPlus} className="size-4" />
        Add {label}
      </a>
    </Button>
  );
}

export function DataWidgetEmptyState({
  blockType,
  strategy,
  hasDynamicRecords = false,
  hasActiveFilters = false,
}: {
  blockType: DataWidgetBlockType;
  strategy?: DataWidgetStrategy;
  hasDynamicRecords?: boolean;
  hasActiveFilters?: boolean;
}) {
  const isPageEditor = useIsPageEditor();
  if (!isPageEditor) return null;

  const config = EMPTY_STATE_CONFIG.get(blockType);
  if (!config) return null;

  const message = unavailableMessage(config, { strategy, hasDynamicRecords, hasActiveFilters });
  return (
    <div className="flex min-h-[160px] flex-col items-center justify-center gap-3 rounded-lg bg-background py-16 text-center">
      {message ? (
        <>
          <p className="text-sm font-medium text-muted-foreground">{message.title}</p>
          <p className="text-xs text-muted-foreground">{message.hint}</p>
        </>
      ) : (
        <>
          <p className="text-sm font-medium text-muted-foreground">No {config.label} Available</p>
          <p className="text-xs text-muted-foreground">
            Add {config.label.toLowerCase()} to see them displayed here.
          </p>
          {config.kind === 'link' ? (
            <AddRecordsLink label={config.label} href={config.getHref()} />
          ) : (
            <AddPageModal
              initialSidebarSelection={config.initialSidebarSelection}
              chainedChildPageType={config.chainedChildPageType}
              openInNewTab
              trigger={<AddRecordsButton label={config.label} />}
            />
          )}
        </>
      )}
    </div>
  );
}
